import classNames from 'classnames';
import { type FocusEvent, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction, FC } from 'react';

import './HeaderNotificationMenuItem.scss';
import HeaderNotificationDrawer from './components/header-notification-drawer/HeaderNotificationDrawer.tsx';
import useNotificationApi from '../../_helpers/hooks/useNotificationApi.ts';
import { NotificationsData } from '../../../../../../components/blackrock/advisor-center/_types/header.ts';
import closeOnFocusOutside from '../../_helpers/closeOnFocusOutside.ts';
import { MenuItem } from '../../../../../../components/blackrock/advisor-center/_types/navigation.ts';

export type Props = {
	apiResult: ReturnType<typeof useNotificationApi>;
	notificationsData: NotificationsData;
	signInMenuItem?: MenuItem;
	isDrawerOpen: boolean;
	setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
	isLoggedIn: boolean;
	isMobileView: boolean;
};

const DRAWER_ID = 'notification-drawer';

const HeaderNotificationMenuItem: FC<Props> = ({
	apiResult,
	notificationsData,
	isDrawerOpen,
	setIsDrawerOpen,
	isLoggedIn,
	isMobileView,
	signInMenuItem,
}) => {
	const menuItemRef = useRef<HTMLDivElement>(null);
	const bellButtonRef = useRef<HTMLButtonElement>(null);

	const onNotificationMenuBlur = (event: FocusEvent<HTMLDivElement>) =>
		closeOnFocusOutside(
			event,
			setIsDrawerOpen,
			event.currentTarget,
			null,
			isDrawerOpen,
			() => {
				/**
				 * focus bell button when drawer is closed via tab after last notification element
				 */
				if ((event.target as HTMLElement) !== bellButtonRef.current) {
					bellButtonRef.current?.focus();
				}
			}
		);

	/**
	 * on mobile, drawer element is not immediately after the navbar link element, so manually setting focus to drawer
	 */
	useEffect(() => {
		if (isDrawerOpen && isMobileView) {
			menuItemRef.current?.focus();
		}
	}, [isDrawerOpen]);

	const onMarkAllAsReadButtonClick = () => {
		menuItemRef.current?.focus();
	};

	return (
		<div
			className="blk-ac-header-notification-menu-item"
			onBlur={onNotificationMenuBlur}
			tabIndex={-1}
			ref={menuItemRef}
		>
			<button
				ref={bellButtonRef}
				aria-controls={DRAWER_ID}
				className={classNames('notification-bell-button', 'front-of-bg', {
					hidden: isMobileView,
				})}
				onClick={() => setIsDrawerOpen((prevValue) => !prevValue)}
				tabIndex={0} // Safari does not include button in tab order if tabIndex is not set
				aria-label={notificationsData.menuItem?.ariaLabel}
				data-link-event={notificationsData.menuItem?.dataLinkEvent}
				aria-expanded={isDrawerOpen}
			>
				<i
					className={
						'icon-bell ' +
						(notificationsData.menuItem?.icon?.path ?? 'fa fa-bell')
					}
					tabIndex={-1}
				>
					{!!apiResult.newCountLabel && (
						<span className="badge">{apiResult.newCountLabel}</span>
					)}
				</i>
			</button>

			{isDrawerOpen && (
				<div className="notification-drawer-wrapper">
					<HeaderNotificationDrawer
						apiResult={apiResult}
						notificationsData={notificationsData}
						id={DRAWER_ID}
						closeDrawer={() => setIsDrawerOpen(false)}
						isLoggedIn={isLoggedIn}
						signInMenuItem={signInMenuItem}
						onMarkAllAsReadButtonClick={onMarkAllAsReadButtonClick}
					/>
				</div>
			)}
		</div>
	);
};

export default HeaderNotificationMenuItem;
